import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Truck, X, CheckCircle2, AlertTriangle, XCircle, Save, Eye, Trash2, Droplet, CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";
import { QualityDot } from "@/components/Quality";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const emptyForm = {
  reception_date: todayISO(),
  supplier_id: "",
  transporter_name: "",
  transporter_plate: "",
  weight_kg: "",
  density: "1.030",
  liters: "",
  temperature_c: "",
  ph: "",
  acidity: "",
  fat_pct: "",
  protein_pct: "",
  purchase_price_per_l: "",
  transport_cost: "0",
  unload_cost: "0",
  payment_method: "contado",
  credit_days: 0,
  account_id: "",
  observations: "",
};

export default function Recepcion() {
  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="recepcion-page">
      <div>
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900">Recepción</h1>
        <p className="text-sm text-stone-500 mt-1">Registro de leche, crema y cuajada compradas a proveedor.</p>
      </div>
      <Tabs defaultValue="leche">
        <TabsList className="bg-stone-100 p-1 rounded-lg">
          <TabsTrigger value="leche" data-testid="tab-leche" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Leche</TabsTrigger>
          <TabsTrigger value="crema" data-testid="tab-crema" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Crema</TabsTrigger>
          <TabsTrigger value="cuajada" data-testid="tab-cuajada" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Cuajada</TabsTrigger>
        </TabsList>
        <TabsContent value="leche" className="mt-4"><LecheTab /></TabsContent>
        <TabsContent value="crema" className="mt-4"><CremaCuajadaTab product="cream" /></TabsContent>
        <TabsContent value="cuajada" className="mt-4"><CremaCuajadaTab product="curd" /></TabsContent>
      </Tabs>
    </div>
  );
}

function LecheTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewCosts = user?.role === "dueno" || user?.role === "admin" || !!user?.permissions?.recepcion?.view_costs;
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const [params, setParams] = useState({ milk_density_kg_per_l: 1.030 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [phInfo, setPhInfo] = useState(null);
  const [quality, setQuality] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastEdited, setLastEdited] = useState("kg");
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [r, s, p, ta] = await Promise.all([
      api.get("/receptions"), api.get("/suppliers"), api.get("/parameters"), api.get("/treasury/accounts"),
    ]);
    setItems(r.data);
    setSuppliers(s.data);
    setParams(p.data);
    setTreasuryAccounts(ta.data.filter((a) => a.active !== false));
    setForm((f) => ({ ...f, density: String(p.data.milk_density_kg_per_l) }));
  };
  useEffect(() => { load(); }, []);

  // pH -> acidez en vivo
  useEffect(() => {
    if (!form.ph) { setPhInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/conversion/ph-to-acidity?ph=${parseFloat(form.ph)}`);
        setPhInfo(data);
        setForm((f) => ({ ...f, acidity: data.acidity.toString() }));
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [form.ph]);

  // Semaforo en vivo
  useEffect(() => {
    const t = setTimeout(async () => {
      const has = form.fat_pct || form.ph || form.acidity || form.temperature_c;
      if (!has) { setQuality(null); return; }
      try {
        const { data } = await api.post("/quality/evaluate", {
          fat_pct: form.fat_pct ? parseFloat(form.fat_pct) : null,
          ph: form.ph ? parseFloat(form.ph) : null,
          acidity: form.acidity ? parseFloat(form.acidity) : null,
          temperature_c: form.temperature_c ? parseFloat(form.temperature_c) : null,
        });
        setQuality(data);
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [form.fat_pct, form.ph, form.acidity, form.temperature_c]);

  // Conversión bidireccional (convención de la planta):
  //   • litros × densidad = kg
  //   • kg ÷ densidad = litros
  useEffect(() => {
    const d = parseFloat(form.density);
    if (!d) return;
    if (lastEdited === "kg") {
      const w = parseFloat(form.weight_kg);
      if (!w) return;
      const newLiters = (w / d).toFixed(3);
      if (newLiters !== form.liters) setForm((f) => ({ ...f, liters: newLiters }));
    } else if (lastEdited === "liters") {
      const l = parseFloat(form.liters);
      if (!l) return;
      const newKg = (l * d).toFixed(3);
      if (newKg !== form.weight_kg) setForm((f) => ({ ...f, weight_kg: newKg }));
    }
    // eslint-disable-next-line
  }, [form.weight_kg, form.liters, form.density, lastEdited]);

  const onWeightChange = (v) => { setLastEdited("kg"); setForm({ ...form, weight_kg: v }); };
  const onLitersChange = (v) => { setLastEdited("liters"); setForm({ ...form, liters: v }); };

  // Al elegir proveedor conocido, autorrellenar forma de pago/días de crédito con su perfil.
  // Si es un proveedor nuevo o de "leche de oportunidad" (sin crédito configurado), queda en
  // Contado y todos los campos siguen editables para capturar el trato puntual.
  const pickSupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    if (!s) return;
    setForm((f) => ({
      ...f,
      supplier_id: s.id,
      credit_days: s.default_credit_days || 0,
      payment_method: (s.default_credit_days || 0) > 0 ? "credito" : "contado",
    }));
  };

  const milkCost = useMemo(() => (parseFloat(form.liters) || 0) * (parseFloat(form.purchase_price_per_l) || 0), [form.liters, form.purchase_price_per_l]);
  const transportTotal = useMemo(() => parseFloat(form.transport_cost) || 0, [form.transport_cost]);
  const unloadTotal = useMemo(() => parseFloat(form.unload_cost) || 0, [form.unload_cost]);
  const initialLotCost = useMemo(() => milkCost + transportTotal + unloadTotal, [milkCost, transportTotal, unloadTotal]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/receptions/${toDelete.id}`);
      toast.success(`Lote ${toDelete.folio} eliminado`);
      setToDelete(null);
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.supplier_id || !form.weight_kg) { toast.error("Proveedor y peso son requeridos"); return; }
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        transporter_name: form.transporter_name,
        transporter_plate: form.transporter_plate,
        reception_date: form.reception_date,
        liters: form.liters ? parseFloat(form.liters) : null,
        weight_kg: parseFloat(form.weight_kg),
        density: parseFloat(form.density) || null,
        temperature_c: form.temperature_c ? parseFloat(form.temperature_c) : null,
        fat_pct: form.fat_pct ? parseFloat(form.fat_pct) : null,
        protein_pct: form.protein_pct ? parseFloat(form.protein_pct) : null,
        ph: form.ph ? parseFloat(form.ph) : null,
        acidity: form.acidity ? parseFloat(form.acidity) : null,
        purchase_price_per_l: form.purchase_price_per_l ? parseFloat(form.purchase_price_per_l) : null,
        transport_cost: transportTotal,
        unload_cost: unloadTotal,
        payment_method: form.payment_method,
        credit_days: form.payment_method === "credito" ? parseInt(form.credit_days) || 0 : 0,
        account_id: form.payment_method === "contado" ? (form.account_id || null) : null,
        observations: form.observations,
      };
      const { data } = await api.post("/receptions", payload);
      toast.success(`Recepción creada: ${data.folio}${data.balance > 0 ? ` · saldo ${fmtMoney(data.balance)}` : ""}`);
      setForm({ ...emptyForm, density: String(params.milk_density_kg_per_l) });
      setPhInfo(null);
      setQuality(null);
      setOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-stone-500">Registro de pipas con conversión automática y semáforo de calidad en vivo.</p>
        </div>
        {!open && (
          <Button onClick={() => setOpen(true)} data-testid="new-reception-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva recepción
          </Button>
        )}
      </div>

      {open && (
        <Card className="border-stone-200 bg-white shadow-sm max-w-5xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
            <h2 className="font-outfit font-semibold text-lg text-stone-900">Nueva Recepción de Leche</h2>
            <button onClick={() => setOpen(false)} data-testid="close-form-btn" className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
          </div>

          <form onSubmit={submit} className="p-6 space-y-6">
            {/* IDENTIFICACION */}
            <section>
              <SectionTitle>Identificación</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Fecha">
                  <Input type="date" value={form.reception_date} onChange={(e) => setForm({ ...form, reception_date: e.target.value })} data-testid="reception-date" />
                </Field>
                <Field label="Proveedor *">
                  <Select value={form.supplier_id} onValueChange={pickSupplier}>
                    <SelectTrigger data-testid="reception-supplier"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.default_credit_days > 0 ? ` · ${s.default_credit_days}d crédito` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Transportista">
                  <Input value={form.transporter_name} onChange={(e) => setForm({ ...form, transporter_name: e.target.value })} placeholder="Nombre" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Field label="No. unidad / placa">
                  <Input value={form.transporter_plate} onChange={(e) => setForm({ ...form, transporter_plate: e.target.value })} placeholder="Ej. ABC-123 / Unidad 04" data-testid="reception-plate" />
                </Field>
              </div>
            </section>

            {/* VOLUMEN */}
            <section>
              <SectionTitle>Volumen <span className="text-stone-400 font-normal normal-case tracking-normal">(conversión automática)</span></SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Peso Neto (kg) *" sub="kg ÷ densidad = litros">
                  <Input type="number" step="0.01" value={form.weight_kg} onChange={(e) => onWeightChange(e.target.value)} data-testid="reception-kg" />
                </Field>
                <Field label="Densidad (g/mL)">
                  <Input type="number" step="0.001" value={form.density} onChange={(e) => setForm({ ...form, density: e.target.value })} data-testid="reception-density" />
                </Field>
                <Field label="Litros" sub="litros × densidad = kg">
                  <Input type="number" step="0.01" value={form.liters} onChange={(e) => onLitersChange(e.target.value)} data-testid="reception-liters" />
                </Field>
              </div>
            </section>

            {/* PARAMETROS DE CALIDAD */}
            <section>
              <SectionTitle>Parámetros de Calidad</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Field label="Temperatura (°C)">
                  <Input type="number" step="0.1" value={form.temperature_c} onChange={(e) => setForm({ ...form, temperature_c: e.target.value })} data-testid="reception-temp" />
                </Field>
                <Field label="pH">
                  <Input type="number" step="0.01" value={form.ph} onChange={(e) => setForm({ ...form, ph: e.target.value })} data-testid="reception-ph" />
                  {phInfo && <p className="text-[11px] text-[#1E3A8A] mt-1">Acidez auto: <b>{phInfo.acidity}°D</b></p>}
                  {phInfo && <p className="text-[11px] text-stone-500">{phInfo.estado_cuajada}</p>}
                </Field>
                <Field label={<>Acidez (°D) {phInfo && <Badge>auto</Badge>}</>}>
                  <Input type="number" step="0.1" value={form.acidity} onChange={(e) => setForm({ ...form, acidity: e.target.value })} data-testid="reception-acidity" />
                </Field>
                <Field label={<>Grasa (%) {form.liters && form.fat_pct && <Badge>auto</Badge>}</>}>
                  <Input type="number" step="0.01" value={form.fat_pct} onChange={(e) => setForm({ ...form, fat_pct: e.target.value })} data-testid="reception-fat" />
                  {form.liters && form.fat_pct && (
                    <p className="text-[11px] text-[#1E3A8A] mt-1" data-testid="fat-points">
                      Puntos de grasa: <b>{((parseFloat(form.liters) || 0) * (parseFloat(form.fat_pct) || 0) / 100).toLocaleString("es-MX", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</b>
                    </p>
                  )}
                  {form.liters && form.fat_pct && (
                    <p className="text-[10px] text-stone-500">= {parseFloat(form.liters).toLocaleString("es-MX")} L × {form.fat_pct}%</p>
                  )}
                </Field>
                <Field label="Proteína (%)">
                  <Input type="number" step="0.01" value={form.protein_pct} onChange={(e) => setForm({ ...form, protein_pct: e.target.value })} data-testid="reception-protein" />
                  {form.protein_pct && (() => {
                    const v = parseFloat(form.protein_pct);
                    const cfg = v >= 3.20 ? { c: "text-emerald-700", l: v >= 3.40 ? "Excelente · Alto rendimiento" : "Buena · Rendimiento normal" }
                      : v >= 3.00 ? { c: "text-amber-700", l: "Aceptable · rendimiento reducido" }
                      : v >= 2.80 ? { c: "text-orange-700", l: "Baja · menor rendimiento" }
                      : { c: "text-red-700", l: "Deficiente · posible adulteración" };
                    return <p className={`text-[11px] mt-1 ${cfg.c}`}><b>{cfg.l}</b></p>;
                  })()}
                </Field>
              </div>
              {quality && <SemaforoBanner quality={quality} />}
            </section>

            {/* COSTOS */}
            <section>
              <SectionTitle>Costos de Compra</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Precio Compra ($/L)">
                  <Input type="number" step="0.01" value={form.purchase_price_per_l} onChange={(e) => setForm({ ...form, purchase_price_per_l: e.target.value })} data-testid="reception-price-l" />
                </Field>
                <Field label="Costo Transporte (MXN)">
                  <Input type="number" step="0.01" value={form.transport_cost} onChange={(e) => setForm({ ...form, transport_cost: e.target.value })} />
                </Field>
                <Field label="Costo Descarga (MXN)">
                  <Input type="number" step="0.01" value={form.unload_cost} onChange={(e) => setForm({ ...form, unload_cost: e.target.value })} />
                </Field>
                <Field label="Forma de pago" sub={form.payment_method === "credito" ? "" : "Proveedor nuevo o leche de oportunidad: deja Contado o cambia a Crédito"}>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v, credit_days: v === "contado" ? 0 : form.credit_days, account_id: v === "credito" ? "" : form.account_id })}>
                    <SelectTrigger data-testid="reception-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contado">Contado</SelectItem>
                      <SelectItem value="credito">Crédito</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Días de crédito">
                  <Input type="number" min="0" disabled={form.payment_method === "contado"} value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: e.target.value })} data-testid="reception-credit-days" placeholder={form.payment_method === "contado" ? "N/A" : "Ej. 15, 30..."} />
                </Field>
                {form.payment_method === "contado" && (
                  <Field label="Cuenta de origen del pago" sub="Opcional: si no la sabes ahora, quedará pendiente de conciliar en Cuentas por Pagar">
                    <Select value={form.account_id || "_none"} onValueChange={(v) => setForm({ ...form, account_id: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="reception-account"><SelectValue placeholder="¿De dónde salió el efectivo?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Aún no sé / registrar después —</SelectItem>
                        {treasuryAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <div className="md:col-span-3 rounded-md border border-stone-200 bg-stone-50/60 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">Costo Inicial del Lote</div>
                  <div className="text-xl font-outfit font-bold text-stone-900 tabular-nums mt-1" data-testid="initial-lot-cost">{fmtMoney(initialLotCost)}</div>
                  <div className="text-[11px] text-stone-500 mt-1">Leche {fmtMoney(milkCost)} + Transporte {fmtMoney(transportTotal)} + Descarga {fmtMoney(unloadTotal)}</div>
                </div>
              </div>
            </section>

            {/* OBSERVACIONES */}
            <section>
              <SectionTitle>Observaciones</SectionTitle>
              <Textarea value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} rows={3} />
            </section>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="reception-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Guardando..." : "Registrar Recepción"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!open && (
        <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="receptions-table">
            <thead>
              <tr className="bg-stone-100 border-b border-stone-200">
                {(canViewCosts ? ["Folio", "Fecha", "Proveedor", "Litros", "Kilos", "Grasa", "pH", "Costo lote", "Pago", "Saldo", "Calidad", "Acciones"] : ["Folio", "Fecha", "Proveedor", "Litros", "Kilos", "Grasa", "pH", "Calidad", "Acciones"]).map((h) => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={canViewCosts ? 12 : 9} className="px-4 py-8 text-center text-sm text-stone-500">No hay recepciones todavía.</td></tr>
              ) : items.map((r) => {
                const overdue = (r.balance || 0) > 0 && r.due_date && new Date(r.due_date) < new Date();
                return (
                <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A]"><div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> {r.folio}</div></td>
                  <td className="px-3 py-3 text-sm text-stone-700">{r.reception_date || new Date(r.created_at).toLocaleDateString("es-MX")}</td>
                  <td className="px-3 py-3 text-sm text-stone-700">{r.supplier_name}</td>
                  <td className="px-3 py-3 text-sm tabular-nums font-medium text-stone-900">{r.liters?.toLocaleString("es-MX")}</td>
                  <td className="px-3 py-3 text-sm tabular-nums text-stone-700">{r.weight_kg?.toLocaleString("es-MX")}</td>
                  <td className="px-3 py-3 text-sm tabular-nums text-stone-700">{r.fat_pct ? `${r.fat_pct}%` : "—"}</td>
                  <td className="px-3 py-3 text-sm tabular-nums text-stone-700">{r.ph ?? "—"}</td>
                  {canViewCosts && (
                  <td className="px-3 py-3 text-sm tabular-nums text-stone-900 font-medium">{r.initial_lot_cost ? `$${r.initial_lot_cost.toLocaleString("es-MX")}` : "—"}</td>
                  )}
                  {canViewCosts && (
                  <td className="px-3 py-3 text-sm" data-testid={`payment-badge-${r.folio}`}>
                    {r.payment_method === "credito"
                      ? <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">Crédito {r.credit_days}d</span>
                      : (r.purchase_cost > 0 && !r.treasury_movement_id)
                        ? <span className="inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-xs font-medium" title="Pagado, pero falta registrar de qué cuenta salió el efectivo">Contado · falta cuenta</span>
                        : <span className="text-stone-500 text-xs">Contado</span>}
                  </td>
                  )}
                  {canViewCosts && (
                  <td className="px-3 py-3 text-sm tabular-nums">
                    {(r.balance || 0) > 0 ? (
                      <div>
                        <span className="text-amber-700 font-semibold">{fmtMoney(r.balance)}</span>
                        {r.due_date && <div className={`text-[10px] ${overdue ? "text-red-600 font-semibold" : "text-stone-500"}`}>vence {r.due_date}{overdue && " ⚠"}</div>}
                      </div>
                    ) : <span className="text-stone-400">—</span>}
                  </td>
                  )}
                  <td className="px-3 py-3 text-sm">
                    {r.quality?.overall?.status ? (
                      <div className="flex items-center gap-1.5"><QualityDot status={r.quality.overall.status} /><span className="text-xs text-stone-600">{r.quality.overall.label}</span></div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigate(`/trazabilidad?lote=${encodeURIComponent(r.folio)}`)}
                        data-testid={`view-lot-${r.folio}`}
                        title="Ver trazabilidad 360°"
                        className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setToDelete(r)}
                        data-testid={`delete-lot-${r.folio}`}
                        title="Eliminar lote"
                        className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar lote {toDelete?.folio}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente la recepción de <b>{toDelete?.supplier_name}</b> ({toDelete?.liters?.toLocaleString("es-MX")} L)
              junto con todas sus muestras de laboratorio, correcciones y producciones asociadas. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700 text-white">
              Eliminar lote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="text-[10px] font-outfit font-semibold uppercase tracking-[0.15em] text-stone-500 mb-3">{children}</h3>
);

const Field = ({ label, sub, children }) => (
  <div>
    <Label className="text-xs font-ibm-plex text-stone-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {sub && <p className="text-[11px] text-stone-500 mt-1">{sub}</p>}
  </div>
);

const Badge = ({ children }) => (
  <span className="inline-block ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-[#1E3A8A]/10 text-[#1E3A8A] align-middle">{children}</span>
);

const SemaforoBanner = ({ quality }) => {
  const s = quality.overall.status;
  const cfg = s === "green" ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", Icon: CheckCircle2, dot: "bg-emerald-500" }
    : s === "yellow" ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", Icon: AlertTriangle, dot: "bg-amber-500" }
    : { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", Icon: XCircle, dot: "bg-red-500" };
  return (
    <div className={`mt-4 rounded-lg border ${cfg.border} ${cfg.bg} p-4`} data-testid="reception-semaforo">
      <div className="flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
        <cfg.Icon className={`w-4 h-4 ${cfg.text}`} />
        <span className={`font-outfit font-semibold ${cfg.text}`}>Semáforo de Calidad: {quality.overall.label}</span>
      </div>
      <div className={`text-xs ${cfg.text} mt-1.5 ml-5`}>
        {Object.entries(quality.fields).map(([k, v]) =>
          `${k === "fat_pct" ? "Grasa" : k === "temperature_c" ? "Temp" : k === "ph" ? "pH" : "Acidez"}: ${v.label}`
        ).join(" · ")}
      </div>
    </div>
  );
};

// ============================================================
// RECEPCIÓN DE CREMA / CUAJADA COMPRADA A PROVEEDOR
// ============================================================
const productLabel = { cream: "Crema", curd: "Cuajada" };
const productFolioPrefix = { cream: "CREC-", curd: "CUAC-" };

const emptyCremaCuajadaForm = {
  supplier_id: "",
  supplier_name: "",
  kg: "",
  liters: "",
  amount: "",
  reception_date: todayISO(),
  payment_method: "contado",
  credit_days: 0,
  invoice_number: "",
  notes: "",
};

function CremaCuajadaTab({ product }) {
  const navigate = useNavigate();
  const label = productLabel[product];
  const hasCalculator = product === "curd";
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [density, setDensity] = useState(1.030);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCremaCuajadaForm);
  const [lastEdited, setLastEdited] = useState("kg");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [r, s, p] = await Promise.all([
      api.get(`/cream-curd-receptions?product=${product}`), api.get("/suppliers"), api.get("/parameters"),
    ]);
    setItems(r.data);
    setSuppliers(s.data);
    setDensity(p.data.curd_density_kg_per_l || 1.030);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Calculadora bidireccional litros<->kg (solo cuajada)
  useEffect(() => {
    if (!hasCalculator || !density) return;
    if (lastEdited === "kg") {
      const w = parseFloat(form.kg);
      if (!w) return;
      const newLiters = (w / density).toFixed(3);
      if (newLiters !== form.liters) setForm((f) => ({ ...f, liters: newLiters }));
    } else if (lastEdited === "liters") {
      const l = parseFloat(form.liters);
      if (!l) return;
      const newKg = (l * density).toFixed(3);
      if (newKg !== form.kg) setForm((f) => ({ ...f, kg: newKg }));
    }
    // eslint-disable-next-line
  }, [form.kg, form.liters, density, lastEdited, hasCalculator]);

  const onKgChange = (v) => { setLastEdited("kg"); setForm({ ...form, kg: v }); };
  const onLitersChange = (v) => { setLastEdited("liters"); setForm({ ...form, liters: v }); };

  const pickSupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    if (!s) return;
    setForm((f) => ({
      ...f, supplier_id: s.id, supplier_name: s.name,
      credit_days: s.default_credit_days || 0,
      payment_method: (s.default_credit_days || 0) > 0 ? "credito" : "contado",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) return toast.error("Selecciona un proveedor");
    if (!(parseFloat(form.kg) > 0)) return toast.error(`Captura los kg de ${label.toLowerCase()} recibidos`);
    if (!(parseFloat(form.amount) > 0)) return toast.error("Captura el costo total");
    setSaving(true);
    try {
      const payload = {
        product,
        supplier_id: form.supplier_id,
        supplier_name: form.supplier_name,
        kg: parseFloat(form.kg),
        liters: hasCalculator && form.liters ? parseFloat(form.liters) : null,
        amount: parseFloat(form.amount),
        reception_date: form.reception_date,
        payment_method: form.payment_method,
        credit_days: form.payment_method === "credito" ? parseInt(form.credit_days) || 0 : 0,
        invoice_number: form.invoice_number,
        notes: form.notes,
      };
      const { data } = await api.post("/cream-curd-receptions", payload);
      toast.success(`Lote ${data.folio} registrado${data.balance > 0 ? ` · saldo $${data.balance.toLocaleString("es-MX")}` : ""} · agregado a Inventario`);
      setForm(emptyCremaCuajadaForm);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/cream-curd-receptions/${toDelete.id}`);
      toast.success(`Lote ${toDelete.folio} eliminado`);
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <p className="text-sm text-stone-500">Compra de {label.toLowerCase()} a proveedor (no producida en planta) — se agrega al inventario disponible para venta.</p>
        {!open && (
          <Button onClick={() => setOpen(true)} data-testid={`new-${product}-reception-btn`} className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva recepción de {label.toLowerCase()}
          </Button>
        )}
      </div>

      {open && (
        <Card className="border-stone-200 bg-white shadow-sm max-w-3xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
            <h2 className="font-outfit font-semibold text-lg text-stone-900">Nueva recepción de {label.toLowerCase()}</h2>
            <button onClick={() => setOpen(false)} data-testid={`close-${product}-form-btn`} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Proveedor *">
                <Select value={form.supplier_id} onValueChange={pickSupplier}>
                  <SelectTrigger data-testid={`${product}-supplier`}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.default_credit_days > 0 ? ` · ${s.default_credit_days}d crédito` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Fecha">
                <Input type="date" value={form.reception_date} onChange={(e) => setForm({ ...form, reception_date: e.target.value })} />
              </Field>
              <Field label="Factura #">
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
              </Field>
            </div>

            <div className={`grid grid-cols-1 gap-4 ${hasCalculator ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              <Field label="Kg recibidos *" sub={hasCalculator ? "kg ÷ densidad = litros" : undefined}>
                <Input type="number" step="0.01" value={form.kg} onChange={(e) => hasCalculator ? onKgChange(e.target.value) : setForm({ ...form, kg: e.target.value })} data-testid={`${product}-kg`} />
              </Field>
              {hasCalculator && (
                <Field label="Litros" sub="litros × densidad = kg">
                  <Input type="number" step="0.01" value={form.liters} onChange={(e) => onLitersChange(e.target.value)} data-testid={`${product}-liters`} />
                </Field>
              )}
              <Field label="Costo total (MXN) *">
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`${product}-amount`} />
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Forma de pago">
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v, credit_days: v === "contado" ? 0 : form.credit_days })}>
                  <SelectTrigger data-testid={`${product}-payment-method`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Días de crédito">
                <Input type="number" min="0" disabled={form.payment_method === "contado"} value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: e.target.value })} placeholder={form.payment_method === "contado" ? "N/A" : "Ej. 15, 30..."} />
              </Field>
            </div>

            <Field label="Notas">
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </Field>

            <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid={`${product}-submit-btn`} className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Guardando..." : "Registrar recepción"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!open && (
        <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid={`${product}-receptions-table`}>
              <thead>
                <tr className="bg-stone-100 border-b border-stone-200">
                  {["Folio", "Fecha", "Proveedor", "Kg", "Costo", "Pago", "Saldo", "Acciones"].map((h) => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-stone-500">No hay recepciones de {label.toLowerCase()} todavía.</td></tr>
                ) : items.map((r) => {
                  const overdue = (r.balance || 0) > 0 && r.due_date && new Date(r.due_date) < new Date();
                  return (
                    <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A]"><div className="flex items-center gap-2"><Droplet className="w-3.5 h-3.5" /> {r.folio}</div></td>
                      <td className="px-3 py-3 text-sm text-stone-700">{r.purchase_date}</td>
                      <td className="px-3 py-3 text-sm text-stone-700">{r.supplier_name}</td>
                      <td className="px-3 py-3 text-sm tabular-nums font-medium text-stone-900">{r.kg?.toLocaleString("es-MX")}{r.liters ? ` (${r.liters.toLocaleString("es-MX")} L)` : ""}</td>
                      <td className="px-3 py-3 text-sm tabular-nums text-stone-900">{fmtMoney(r.total_cost)}</td>
                      <td className="px-3 py-3 text-xs">
                        {r.payment_method === "credito"
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"><CreditCard className="w-3 h-3" /> {r.credit_days}d</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium"><Wallet className="w-3 h-3" /> Contado</span>}
                      </td>
                      <td className="px-3 py-3 text-sm tabular-nums">
                        {(r.balance || 0) > 0 ? (
                          <div>
                            <span className="text-amber-700 font-semibold">{fmtMoney(r.balance)}</span>
                            {r.due_date && <div className={`text-[10px] ${overdue ? "text-red-600 font-semibold" : "text-stone-500"}`}>vence {r.due_date}{overdue && " ⚠"}</div>}
                          </div>
                        ) : <span className="text-stone-400">—</span>}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/trazabilidad?lote=${encodeURIComponent(r.folio)}`)}
                            title="Ver en Inventario/Trazabilidad"
                            className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setToDelete(r)}
                            data-testid={`delete-${product}-${r.folio}`}
                            title="Eliminar lote"
                            className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar lote {toDelete?.folio}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente este lote de {label.toLowerCase()} comprada. No se puede deshacer si ya tiene kg vendidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid={`confirm-delete-${product}-btn`} className="bg-red-600 hover:bg-red-700 text-white">
              Eliminar lote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
